import { defineBoot } from "#q-app/wrappers";
import { LoadingBar, debounce } from "quasar";
import { getAccessToken } from "supertokens-web-js/recipe/session";

import { offline } from "./offline";
import { refreshToken } from "src/api/auth";
import {
  api,
  searchApi,
  conversationsApi,
  CancelToken,
  notify,
  errorLogger,
  showGenericToast,
} from "assets/scripts/utilities/api-clients";

const coreApiDomain = import.meta.env.CLIENT_API_DOMAIN;

const expireSession = debounce((reset) => {
  notify({
    message: "Your session has expired. Please login again.",
    type: "negative",
  });

  if (!api.defaults.baseURL.includes(coreApiDomain)) {
    delete api.defaults.headers.common["Authorization"];

    delete searchApi.defaults.headers.common["Authorization"];

    delete conversationsApi.defaults.headers.common["Authorization"];
  }

  reset();
}, 1000);

let refreshingToken = false;
const refreshAndRetryQueue = [];

const rejectQueuedRequests = (error) => {
  refreshAndRetryQueue.forEach(({ reject }) => {
    reject(error);
  });

  refreshAndRetryQueue.length = 0;
};

const refreshSession = async ({ apiInstance, error, reset }) => {
  const originalRequest = error.config;

  if (!refreshingToken) {
    refreshingToken = true;

    try {
      const success = await refreshToken();

      if (success) {
        let accessToken;

        const isManualRefreshRequired =
          !api.defaults.baseURL.includes(coreApiDomain);

        if (isManualRefreshRequired) {
          accessToken = await getAccessToken();

          accessToken = `Bearer ${accessToken}`;

          apiInstance.defaults.headers.common["Authorization"] = accessToken;

          originalRequest.headers["Authorization"] = accessToken;
        }

        refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
          if (isManualRefreshRequired) {
            config.headers["Authorization"] = accessToken;
          }

          apiInstance
            .request(config)
            .then((response) => resolve(response))
            .catch((err) => reject(err));
        });

        refreshAndRetryQueue.length = 0;

        return apiInstance(originalRequest);
      } else {
        // Refresh failed - reject all queued requests
        const refreshError = new Error("Session refresh failed");

        refreshError.response = error.response;

        rejectQueuedRequests(refreshError);

        expireSession(reset);

        throw refreshError;
      }
    } catch (refreshError) {
      // Handle unexpected errors during refresh
      rejectQueuedRequests(refreshError);

      expireSession(reset);

      throw refreshError;
    } finally {
      refreshingToken = false;
    }
  }

  return new Promise((resolve, reject) => {
    refreshAndRetryQueue.push({
      config: originalRequest,
      resolve,
      reject,
    });
  });
};

const requestInterceptor = (config) => {
  if (unref(offline)) {
    const error = new Error(
      "You are offline. Please check your internet connection and try again.",
    );

    error.response = {
      data: {
        message:
          "You are offline. Please check your internet connection and try again.",
      },
    };

    throw error;
  }

  return config;
};

const requestErrorHandler = (error) => {
  errorLogger(error);

  throw error;
};

const responseInterceptor = (response) => {
  if (
    response.data?.status?.toLowerCase() === "success" &&
    /\w/g.test(response.data?.message) &&
    [undefined, true].includes(response.config.params?.showMessage)
  ) {
    showGenericToast(response, "positive");
  }

  return response;
};

const responseErrorHandler = async ({ api, error, reset }) => {
  if (error?.response?.status === 401) {
    return refreshSession({
      apiInstance: api,
      error,
      reset,
    });
  }

  errorLogger(error);

  throw error;
};

export default defineBoot(({ app, router, store }) => {
  const { reset } = useAuthStore(store);

  api.interceptors.request.use(requestInterceptor, requestErrorHandler);

  api.interceptors.response.use(responseInterceptor, (error) =>
    responseErrorHandler({ api, error, reset }),
  );

  conversationsApi.interceptors.request.use(
    requestInterceptor,
    requestErrorHandler,
  );

  searchApi.interceptors.request.use(requestInterceptor, requestErrorHandler);

  searchApi.interceptors.response.use(responseInterceptor, (error) =>
    responseErrorHandler({
      api: searchApi,
      error,
      reset,
    }),
  );

  conversationsApi.interceptors.response.use(responseInterceptor, (error) =>
    responseErrorHandler({
      api: conversationsApi,
      error,
      reset,
    }),
  );

  // show loading bar only when navigating to a detail page
  LoadingBar.setDefaults({
    hijackFilter(url) {
      try {
        url = new URL(url);

        const currentRoute = unref(router.currentRoute);

        return url.pathname.includes(currentRoute?.params?.id);
      } catch {
        return false;
      }
    },
  });

  // for Composition API
  app.provide("api", {
    api,
    searchApi,
    conversationsApi,

    CancelToken,

    notify,
    errorLogger,
  });

  app.provide("offline", offline);
});

export {
  api,
  searchApi,
  conversationsApi,
  CancelToken,
  notify,
  errorLogger,
} from "assets/scripts/utilities/api-clients";
